import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Download, FileText, Loader2 } from 'lucide-react';
import { canDownloadPortalClientCv } from '../../utils/portalClientCvDownload';
import type { ProposalRow } from '../../data/clientEngagementMock';
import { formatNlDateTime, proposalStatusLabel } from './engagementLabels';
import InlineFeedbackNote from './InlineFeedbackNote';
import QuickActionButtons, { type QuickClientAction } from './QuickActionButtons';

export interface CandidateProposalCardProps {
  proposal: ProposalRow;
  noteText: string;
  onAutosaveNote: (proposalId: string, text: string) => void;
  onSendHeadsUp: (proposalId: string, text: string) => void;
  onQuickAction: (proposalId: string, action: QuickClientAction) => void | Promise<void>;
  /** Geen klant-acties: recruiter-preview van het klantportaal (zelfde kaart, alleen lezen). */
  readOnlyClientPortalUi?: boolean;
  /** Eenvoudige klantkeuze: uitnodigen ja/nee + toelichting (klantportaal). */
  decisionMode?: boolean;
  onInviteDecision?: (proposalId: string, opts: { accept: boolean; comment: string }) => void | Promise<void>;
  /** Vacaturetitel klikbaar → modal in parent. */
  onVacancyClick?: (vacancyId: string) => void;
  /** CV / kandidaatprofiel openen (klantportaal). */
  onCandidateProfileClick?: () => void;
  /** Download laatste CV (zelfde bron als onder Kandidaten). */
  onDownloadLatestCv?: () => void | Promise<void>;
  /** Klant: stuur bericht in portal-thread (zonder statuswijziging). */
  onPortalThreadReply?: (proposalId: string, text: string) => void | Promise<void>;
}

function statusBadgeClass(status: ProposalRow['status']): string {
  switch (status) {
    case 'waiting_feedback':
      return 'bg-amber-100 text-amber-900 ring-amber-200';
    case 'shortlisted':
      return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
    case 'rejected':
      return 'bg-slate-200 text-slate-700 ring-slate-300';
    case 'more_info_requested':
      return 'bg-indigo-100 text-indigo-900 ring-indigo-200';
    case 'interview_requested':
      return 'bg-sky-100 text-sky-900 ring-sky-200';
    case 'on_hold':
      return 'bg-orange-100 text-orange-900 ring-orange-200';
    default:
      return 'bg-slate-100 text-slate-700 ring-slate-200';
  }
}

const CollapsiblePanel: React.FC<{
  panelId: string;
  title: string;
  defaultOpen: boolean;
  children: React.ReactNode;
  variant?: 'slate' | 'indigo';
}> = ({ panelId, title, defaultOpen, children, variant = 'slate' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const border = variant === 'indigo' ? 'border-indigo-100' : 'border-slate-100';
  const headBg = variant === 'indigo' ? 'bg-indigo-50/40' : 'bg-slate-50/80';
  return (
    <div className={`rounded-xl border ${border} overflow-hidden`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 px-4 py-3 text-left ${headBg} transition hover:bg-slate-100/80`}
      >
        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600">{title}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div id={panelId} className="border-t border-slate-100/80 px-4 py-4">
          {children}
        </div>
      ) : null}
    </div>
  );
};

/** Na een klantbeslissing geen «Stap 1» meer tonen — duidelijke uitleg i.p.v. lege uitnodiging. */
function ClientPortalDecisionResolvedPanel({ proposal }: { proposal: ProposalRow }) {
  const pipeline = proposal.pipelineStatus?.trim();
  switch (proposal.status) {
    case 'rejected':
      return (
        <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-5 shadow-sm ring-1 ring-slate-100/80">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">Beslissing vastgelegd</p>
          <p className="mt-2 text-lg font-bold text-slate-900">Deze kandidaat is niet passend</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Je hoeft hier geen keuze meer te maken: dit voorstel staat al als <strong>niet passend</strong> in het dossier.
            {pipeline ? (
              <>
                {' '}
                Status in pipeline: <span className="font-semibold text-slate-800">{pipeline}</span>.
              </>
            ) : null}
          </p>
          <p className="mt-3 text-xs text-slate-500">
            Vragen of toelichting? Gebruik het blok <strong>Overleg met recruiter</strong> hieronder.
          </p>
        </div>
      );
    case 'shortlisted':
      return (
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-5 shadow-sm ring-1 ring-emerald-100/80">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Op shortlist</p>
          <p className="mt-2 text-base font-semibold text-slate-900">Deze kandidaat staat op de shortlist</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Er is geen openstaande uitnodigingsvraag. {pipeline ? `Pipeline: ${pipeline}.` : ''}
          </p>
        </div>
      );
    case 'on_hold':
      return (
        <div className="rounded-2xl border-2 border-orange-200 bg-orange-50/50 p-5 shadow-sm ring-1 ring-orange-100/80">
          <p className="text-[10px] font-black uppercase tracking-widest text-orange-900">Later beoordelen</p>
          <p className="mt-2 text-base font-semibold text-slate-900">Geen actieve uitnodigingsstap</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Dit dossier staat on hold. {pipeline ? `Pipeline: ${pipeline}.` : ''}
          </p>
        </div>
      );
    case 'more_info_requested':
      return (
        <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-5 shadow-sm ring-1 ring-indigo-100/80">
          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-800">Meer informatie</p>
          <p className="mt-2 text-base font-semibold text-slate-900">Er is om aanvullende informatie gevraagd</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Gebruik <strong>Overleg met recruiter</strong> om te reageren. {pipeline ? `Pipeline: ${pipeline}.` : ''}
          </p>
        </div>
      );
    default:
      return (
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 shadow-sm ring-1 ring-slate-100/80">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Status</p>
          <p className="mt-2 text-base font-semibold text-slate-900">{proposalStatusLabel(proposal.status)}</p>
          {pipeline ? <p className="mt-2 text-sm text-slate-600">Pipeline: {pipeline}</p> : null}
        </div>
      );
  }
}

const CandidateProposalCard: React.FC<CandidateProposalCardProps> = ({
  proposal,
  noteText,
  onAutosaveNote,
  onSendHeadsUp,
  onQuickAction,
  readOnlyClientPortalUi,
  decisionMode,
  onInviteDecision,
  onVacancyClick,
  onCandidateProfileClick,
  onDownloadLatestCv,
  onPortalThreadReply,
}) => {
  const [portalDraft, setPortalDraft] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  const [cvDownloadBusy, setCvDownloadBusy] = useState(false);
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionBusy, setDecisionBusy] = useState<'yes' | 'no' | null>(null);
  /** Standaard alleen de kop (zoals compact clientportaal); open als er een actieve uitnodigingsstap is. */
  const [detailsOpen, setDetailsOpen] = useState(
    () => !!decisionMode && !readOnlyClientPortalUi && proposal.status === 'waiting_feedback',
  );
  const hasCv =
    !!proposal.hasFormattedClientCv ||
    !!(proposal.cvPlainText && proposal.cvPlainText.trim()) ||
    !!(proposal.cvSummary && proposal.cvSummary.trim()) ||
    !!(proposal.aiParsed && Object.keys(proposal.aiParsed).length) ||
    !!(proposal.motivationOriginal && proposal.motivationOriginal.trim());

  const vacancyEl = onVacancyClick ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onVacancyClick(proposal.vacancyId);
      }}
      className="text-left text-sm font-medium text-indigo-600 underline decoration-indigo-200 decoration-1 underline-offset-2 transition hover:text-indigo-800 hover:decoration-indigo-400"
    >
      {proposal.vacancyTitle}
    </button>
  ) : (
    <p className="text-sm font-medium text-slate-500">{proposal.vacancyTitle}</p>
  );

  const threadHasMessages = !!(proposal.portalThread && proposal.portalThread.length > 0);
  const showCvDownload = !!onDownloadLatestCv && canDownloadPortalClientCv(proposal);

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm ring-1 ring-slate-100/80 transition hover:shadow-md">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50/90 to-white px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-sm font-black text-white shadow-md">
              {proposal.initials}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{proposal.name}</h3>
              {vacancyEl}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-lg bg-slate-900 px-2 py-0.5 text-xs font-black text-white">
                  Match {proposal.matchScore}%
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${statusBadgeClass(proposal.status)}`}
                >
                  {proposalStatusLabel(proposal.status)}
                </span>
              </div>
              {proposal.pipelineStatus ? (
                <p className="mt-1.5 text-[11px] font-semibold text-slate-500">Pipeline (database): {proposal.pipelineStatus}</p>
              ) : null}
              {proposal.scheduledInterviewHint ? (
                <p className="mt-1.5 inline-flex max-w-full items-center rounded-md bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-800 ring-1 ring-violet-100">
                  Afspraak: {proposal.scheduledInterviewHint}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            {showCvDownload ? (
              <button
                type="button"
                disabled={cvDownloadBusy}
                onClick={async (e) => {
                  e.stopPropagation();
                  setCvDownloadBusy(true);
                  try {
                    await onDownloadLatestCv?.();
                  } finally {
                    setCvDownloadBusy(false);
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cvDownloadBusy ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
                Download laatste CV
              </button>
            ) : null}
            <dl className="grid gap-1 text-right text-xs text-slate-500 sm:text-left">
            <div>
              <dt className="font-bold uppercase tracking-wider text-slate-400">Ingediend</dt>
              <dd className="font-semibold text-slate-700">{formatNlDateTime(proposal.submittedAt)}</dd>
            </div>
            <div>
              <dt className="font-bold uppercase tracking-wider text-slate-400">Laatste update</dt>
              <dd className="font-semibold text-slate-700">{formatNlDateTime(proposal.lastUpdatedAt)}</dd>
            </div>
          </dl>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
          className="flex w-full items-center justify-center gap-2 border-t border-slate-100 bg-white px-5 py-2.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50 sm:px-6"
        >
          {detailsOpen ? (
            <>
              <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              Minder tonen
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              Details tonen
            </>
          )}
        </button>
      </div>

      {detailsOpen ? (
      <div className="space-y-4 px-5 py-5 sm:px-6">
        <div className="space-y-3">
          <section className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
            <p className="text-[11px] font-black uppercase tracking-wider text-indigo-900">Waarom dit een match is</p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
              {(proposal.matchExplanation || '').trim() || 'Nog geen toelichting waarom dit een match is.'}
            </p>
          </section>
          <section className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
            <p className="text-[11px] font-black uppercase tracking-wider text-slate-600">Feedback van de recruiter</p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
              {(proposal.recruiterPortalSummary || '').trim() || 'Nog geen samenvatting van de recruiter.'}
            </p>
          </section>
        </div>

        {!readOnlyClientPortalUi && decisionMode && onInviteDecision ? (
          proposal.status === 'interview_requested' ? (
            <div className="rounded-2xl border-2 border-sky-300 bg-gradient-to-br from-sky-50 via-white to-white p-5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-sky-800">Interview aangevraagd</p>
              <p className="mt-2 text-base font-semibold text-slate-900">Je verzoek staat bij de recruiter</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Je hoeft hier niets meer te doen. Zodra er een datum is afgestemd, verschijnt het gesprek in je agenda hieronder.
                Heb je nog een vraag? Gebruik dan het overleg met de recruiter.
              </p>
            </div>
          ) : proposal.status === 'waiting_feedback' ? (
            <div className="rounded-2xl border-2 border-indigo-200 bg-white p-5 shadow-md ring-1 ring-indigo-100/80">
              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Stap 1 — Uitnodiging</p>
              <p className="mt-2 text-lg font-bold text-slate-900">Wil je deze kandidaat uitnodigen voor een gesprek?</p>
              <p className="mt-1 text-sm text-slate-600">
                Je keuze wordt direct bij het dossier gezet. Optioneel kun je hieronder nog een korte toelichting toevoegen.
              </p>
              <textarea
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                rows={3}
                placeholder="Optioneel: korte toelichting bij je keuze…"
                className="mt-4 w-full resize-y rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
              />
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={!!decisionBusy}
                  onClick={async () => {
                    setDecisionBusy('yes');
                    try {
                      await onInviteDecision(proposal.proposalId, { accept: true, comment: decisionComment.trim() });
                      setDecisionComment('');
                    } finally {
                      setDecisionBusy(null);
                    }
                  }}
                  className="rounded-xl bg-emerald-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
                >
                  {decisionBusy === 'yes' ? 'Bezig…' : 'Ja, uitnodigen voor gesprek'}
                </button>
                <button
                  type="button"
                  disabled={!!decisionBusy}
                  onClick={async () => {
                    setDecisionBusy('no');
                    try {
                      await onInviteDecision(proposal.proposalId, { accept: false, comment: decisionComment.trim() });
                      setDecisionComment('');
                    } finally {
                      setDecisionBusy(null);
                    }
                  }}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {decisionBusy === 'no' ? 'Bezig…' : 'Nee, geen match'}
                </button>
              </div>
            </div>
          ) : (
            <ClientPortalDecisionResolvedPanel proposal={proposal} />
          )
        ) : null}

        <CollapsiblePanel panelId={`kern-${proposal.proposalId}`} title="Kerngegevens & CV" defaultOpen={false}>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {proposal.skills.map((s) => (
                <span key={s} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                  {s}
                </span>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="text-[11px] font-bold uppercase text-slate-400">Beschikbaarheid</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">{proposal.availability}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="text-[11px] font-bold uppercase text-slate-400">Salarisindicatie</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">{proposal.salaryIndication}</p>
              </div>
            </div>
            {onCandidateProfileClick ? (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCandidateProfileClick();
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-1 text-left text-sm font-bold text-indigo-900 transition hover:bg-indigo-100/50"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    {hasCv ? 'Originele CV & motivatie' : 'Kandidaatgegevens'}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
        </CollapsiblePanel>

        {!readOnlyClientPortalUi ? (
          <CollapsiblePanel panelId={`heads-${proposal.proposalId}`} title="Toelichting voor recruitment (heads-up)" defaultOpen={false}>
            <InlineFeedbackNote
              entityKey={proposal.proposalId}
              initialText={noteText}
              onAutosave={(t) => onAutosaveNote(proposal.proposalId, t)}
              onSendHeadsUp={(t) => onSendHeadsUp(proposal.proposalId, t)}
            />
          </CollapsiblePanel>
        ) : null}

        {readOnlyClientPortalUi ? (
          <CollapsiblePanel panelId={`thread-ro-${proposal.proposalId}`} title="Overleg klant ↔ recruiter" defaultOpen={threadHasMessages}>
            {proposal.portalThread?.length ? (
              <ul className="max-h-52 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-sm">
                {proposal.portalThread.map((m, i) => (
                  <li key={`${m.at}-${i}`} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <span className="text-[10px] font-bold uppercase text-slate-400">
                      {m.author === 'client' ? 'Klant' : 'Recruiter'} · {formatNlDateTime(m.at)}
                    </span>
                    <p className="mt-1 whitespace-pre-wrap text-slate-800">{m.text}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-600">Nog geen berichten in dit dossier.</p>
            )}
          </CollapsiblePanel>
        ) : onPortalThreadReply ? (
          <CollapsiblePanel
            panelId={`thread-${proposal.proposalId}`}
            title="Overleg met recruiter"
            defaultOpen={threadHasMessages}
            variant="indigo"
          >
            {proposal.portalThread?.length ? (
              <ul className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-indigo-100/80 bg-white p-3 text-sm">
                {proposal.portalThread.map((m, i) => (
                  <li key={`${m.at}-${i}`} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <span className="text-[10px] font-bold uppercase text-slate-400">
                      {m.author === 'client' ? 'Jij' : 'Recruiter'} · {formatNlDateTime(m.at)}
                    </span>
                    <p className="mt-1 whitespace-pre-wrap text-slate-800">{m.text}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-600">Nog geen berichten — stel hieronder een vraag of reactie.</p>
            )}
            <textarea
              value={portalDraft}
              onChange={(e) => setPortalDraft(e.target.value)}
              rows={3}
              placeholder="Bericht aan de recruiter…"
              className="mt-3 w-full resize-y rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
            <button
              type="button"
              disabled={portalBusy || !portalDraft.trim()}
              onClick={async () => {
                const t = portalDraft.trim();
                if (!t) return;
                setPortalBusy(true);
                try {
                  await onPortalThreadReply(proposal.proposalId, t);
                  setPortalDraft('');
                } finally {
                  setPortalBusy(false);
                }
              }}
              className="mt-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
            >
              {portalBusy ? 'Verzenden…' : 'Verstuur bericht'}
            </button>
          </CollapsiblePanel>
        ) : null}

        {!readOnlyClientPortalUi && decisionMode && onInviteDecision ? null : readOnlyClientPortalUi ? (
          <p className="rounded-xl border border-slate-100 bg-slate-50/90 px-3 py-2.5 text-center text-xs text-slate-600">
            Alleen-lezen: snelle acties en uitnodigingskeuze zijn alleen beschikbaar voor de klant in het klantportaal.
          </p>
        ) : (
          <QuickActionButtons onAction={(a) => void onQuickAction(proposal.proposalId, a)} />
        )}
      </div>
      ) : null}
    </article>
  );
};

export default CandidateProposalCard;
